import React, { useState, useEffect, useContext, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Card, Chip, Paper, CircularProgress } from '@mui/material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { BASE_URL } from './../../Api/ApiUrls';
import { AuthContext } from '../../AuthContext/AuthContext';
import FormsBottomNavbar from './../Post Module/FormsBottomNavbar';
import CustomSearchBar from "./../Lease Module/CustomSearch";

// Fix for Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const workerTypes = [
  "Painting", "Carpenter", "Flooring", "AC Technician", "Cleaning maid",
  "Gardener", "Sofa Cleaning", "Water Purifier", "Kitchen/Toilet Cleaning", "Plumbing", "Electrical"
];

const DEFAULT_CENTER = [17.3850, 78.4867]; // Hyderabad

const MapWrapper = ({ center, zoom, vendors, onMarkerClick }) => {
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);

  useEffect(() => {
    mapRef.current = L.map(mapContainerRef.current, {
      center,
      zoom,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(mapRef.current);

    vendors.forEach(vendor => {
      if (vendor.lat && vendor.long) {
        const marker = L.marker([parseFloat(vendor.lat), parseFloat(vendor.long)])
          .addTo(mapRef.current)
          .bindPopup(`${vendor.profession}<br>${vendor.name}`)
          .on('click', () => onMarkerClick(vendor));
      }
    });

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [center, zoom, vendors, onMarkerClick]);

  return <div ref={mapContainerRef} style={{ height: '100%', width: '100%' }} />;
};

const HomeService = () => {
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [loading, setLoading] = useState(false);

  const { userId } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const category = location.state?.category || '';

  const containerStyle = {
    width: '100%',
    height: 'calc(100vh - 240px)',
  };

  useEffect(() => {
    const fetchUserLocation = async () => {
      try {
        if (!userId) {
          setMapCenter(DEFAULT_CENTER);
          return;
        }
        const response = await fetch(`${BASE_URL}/users/${userId}/`);
        const userData = await response.json();

        if (userData.lat && userData.long) {
          setMapCenter([parseFloat(userData.lat), parseFloat(userData.long)]);
        } else {
          setMapCenter(DEFAULT_CENTER);
        }
      } catch (error) {
        console.error("Error fetching user location:", error);
        setMapCenter(DEFAULT_CENTER);
      }
    };

    const fetchVendors = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${BASE_URL}/vendors/`);
        const data = await response.json();
        const filtered = category
          ? data.filter(vendor =>
              vendor.profession &&
              vendor.profession.toLowerCase().includes(category.toLowerCase()))
          : data;

        setVendors(filtered);
        setFilteredVendors(filtered);
      } catch (error) {
        console.error("Error fetching vendors:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserLocation();
    fetchVendors();
  }, [category, userId]);

  useEffect(() => {
    if (vendors.length > 0) {
      const filtered = selectedType
        ? vendors.filter(vendor =>
            vendor.profession &&
            vendor.profession.toLowerCase().includes(selectedType.toLowerCase()))
        : vendors;
      setFilteredVendors(filtered);
    }
  }, [selectedType, vendors]);

  const handleTypeSelection = (type) => {
    setSelectedWorker(null);
    setSelectedType(prev => (prev === type ? null : type));
  };

  const handleMarkerClick = (vendor) => {
    setSelectedWorker(vendor);
  };

  return (
    <Box
      sx={{
        pb: 7,
        maxWidth: 480,
        mx: "auto",
        position: 'relative',
        bgcolor: 'rgb(239, 231, 221)',
        minHeight: '100vh'
      }}
    >
      {/* Sticky Search Header */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          bgcolor: 'rgb(239, 231, 221)',
          px: 1,
          py: 1,
        }}
      >
        <CustomSearchBar />
      </Box>

      {/* Worker Type Chips */}
      <Box sx={{ px: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
           {category || 'All'} Services
        </Typography>
       
      </Box>

      {/* Loader */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* No vendors message */}
     {!loading && filteredVendors.length === 0 && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '200px',
          px: 2,
          textAlign: 'center'
        }}>
          <Box sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            padding: '16px',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <Typography variant="h6">
              No workers available in this category
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              We couldn't find any workers matching "{category}"
            </Typography>
          </Box>
        </Box>
      )}

      {/* Map and Worker Card */}
      { filteredVendors.length > 0 && (
        <Box sx={{ px: 2, pb: 10 }}>
          <Box sx={{ width: '100%', height: containerStyle.height }}>
            <MapWrapper
              center={mapCenter}
              zoom={12}
              vendors={filteredVendors}
              onMarkerClick={handleMarkerClick}
            />
          </Box>

          {selectedWorker && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 170,
                left: 0,
                right: 0,
                px: 2,
                zIndex: 999,
              }}
            >
              <Card
                onClick={() => navigate(`/work-details/${selectedWorker.vendor_id}`)}
                sx={{
                  borderRadius: 3,
                  p: 2,
                  backgroundColor: '#fff',
                  boxShadow: 3,
                }}
              >
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <img
                    src={`${BASE_URL}${selectedWorker.profile}`}
                    alt={selectedWorker.name}
                    style={{
                      width: 100,
                      height: 100,
                      borderRadius: 10,
                      objectFit: 'cover',
                    }}
                  />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {selectedWorker.profession}
                    </Typography>
                    <Typography variant="body2">Name: {selectedWorker.name}</Typography>
                    <Typography variant="body2">Mobile: {selectedWorker.mobile}</Typography>
                    <Typography variant="body2">Email: {selectedWorker.email}</Typography>
                    <Typography variant="body2">Experience: {selectedWorker.experience}+ year(s)</Typography>
                    <Typography variant="body2">⭐⭐⭐⭐</Typography>
                  </Box>
                </Box>
              </Card>
            </Box>
          )}
        </Box>
      )}

      {/* Bottom Navbar */}
      <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0 }} elevation={3}>
        <FormsBottomNavbar />
      </Paper>
    </Box>
  );
};

export default HomeService;
